import { useId, useState, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { AppSidebar } from "../AppSidebar/AppSidebar";
import { AppTopbar } from "../AppTopbar/AppTopbar";
import { NavDrawer } from "../NavDrawer/NavDrawer";
import { type NavMap } from "../navigation/navigation";
import { useShellUser } from "./useShellUser";
import { useSidebarCollapsed } from "./useSidebarCollapsed";
import { useSidebarPeek } from "./useSidebarPeek";

export interface AppShellProps {
  /**
   * La navegación, agrupada por secciones. Es la ÚNICA lista: de aquí
   * salen los enlaces de la barra lateral, los del cajón móvil y el
   * título de la barra superior.
   */
  nav: NavMap;
  children: ReactNode;
  className?: string;
}

/**
 * El armazón de la aplicación: barra lateral flotante, barra superior
 * flotante y el contenido en medio.
 *
 * ── El lenguaje visual ───────────────────────────────────────────────
 * La ESTRUCTURA viene de la maqueta de referencia —riel compacto,
 * paneles flotantes, profundidad por capas—; el COLOR y el material, del
 * login y el registro, que es lo que Anahí ya aprobó: crema cálido,
 * vidrio y violeta. La referencia era oscura; copiarle el color habría
 * roto la coherencia con las dos pantallas que ya existen.
 *
 * ── Por qué el fondo es transparente ─────────────────────────────────
 * `globals.css` ya pinta en el `body` el crema y los dos halos violeta
 * fijos. Si este contenedor pintase su propio fondo los taparía, y los
 * paneles de vidrio se quedarían sin nada que difuminar por detrás: el
 * `backdrop-blur` sobre un color plano no se ve. Los halos son justamente
 * lo que hace que el vidrio parezca vidrio.
 *
 * ── Qué API tiene y por qué cambió ───────────────────────────────────
 * Antes recibía `sidebar` y `topbar` ya pintados, y por eso `RootLayout`
 * acababa con la barra lateral y la superior escritas a mano dentro. Ahora
 * recibe DATOS —la lista de navegación— y pinta él las dos barras. Ese es
 * el cambio que deja `RootLayout` en tres líneas. `AppShell` era el único
 * consumidor de la API vieja.
 */
export function AppShell({ nav, children, className }: AppShellProps) {
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const peek = useSidebarPeek(collapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerId = useId();
  const { user, logout } = useShellUser();

  return (
    <div className={cn("flex min-h-dvh w-full", className)}>
      {/*
        La columna de la barra lateral. El ancho está AQUÍ y no en el
        panel para que la transición de plegado la haga el hueco del
        layout: animar el ancho del panel dejaría el contenido saltando a
        su posición final de golpe mientras la barra todavía se encoge.
      */}
      <div
        className={cn(
          "hidden shrink-0 p-3 lg:block xl:p-4",
          "transition-[width] duration-300 ease-[cubic-bezier(0.25,0.1,0.25,1)] motion-reduce:transition-none",
          /*
            La mirada EMPUJA, no se superpone (Anahí, 26-ago).

            La primera versión ensanchaba el panel POR ENCIMA del
            contenido para no recolocar la página, y en la práctica se
            veía como un bug: la barra tapaba la superior y el contenido
            quedaba a medio ver debajo. Ahora la COLUMNA entera crece al
            asomarse —el mismo ancho y la misma curva que al desplegarla
            de verdad— y la barra superior y el `main` se desplazan con
            la transición, como con el botón de plegado.
          */
          collapsed && !peek.peeking ? "w-[6.25rem]" : "w-[17.25rem]",
        )}
      >
        <div
          {...peek.handlers}
          className="sticky top-3 h-[calc(100dvh-1.5rem)] w-full xl:top-4 xl:h-[calc(100dvh-2rem)]"
        >
          <AppSidebar
            nav={nav}
            user={user}
            collapsed={collapsed && !peek.peeking}
            onToggleCollapsed={toggleCollapsed}
          />
        </div>
      </div>

      {/* `min-w-0`: sin esto una tabla ancha estira la columna y desborda
          la página entera en horizontal en vez de desplazarse dentro. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <AppTopbar
          user={user}
          onLogout={logout}
          onOpenNav={() => setDrawerOpen(true)}
          navOpen={drawerOpen}
          navPanelId={drawerId}
        />

        {/*
          `pb-8` y no `pb-20`. Los 80 px de antes eran aire: en una página
          que scrollea da igual, pero en una pensada para caber —el alta de
          un producto, que se recorre por pasos— ese aire es lo que empuja
          la botonera fuera de la pantalla y saca una barra de scroll para
          enseñar un hueco vacío. 32 px siguen separando el final del
          contenido del borde de la ventana.
        */}
        <main className="mx-auto w-full max-w-[90rem] flex-1 px-4 pb-8 pt-4 sm:px-5 xl:px-6">
          {children}
        </main>
      </div>

      <NavDrawer
        id={drawerId}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        nav={nav}
        user={user}
      />
    </div>
  );
}
